import { ref, computed, watch } from 'vue';
import { SelectListItemType } from '@app/enums/SelectListItemType';
import { SelectListItemStateType } from '@app/enums/SelectListItemStateType';
import type ISelectItem from '@app/interfaces/dtos/ISelectItem';
import { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import { UseMultiselectOptions, UseMultiselectReturn } from '@app/interfaces/useMultiselect.interface';
import { toMultiselectOption } from '@app/utils/multiselectOptionMapper';

export const useMultiselect = ({ props, emit }: UseMultiselectOptions): UseMultiselectReturn => {
	const multiselectValue = ref(props.modelValue);
	const isOpen = ref(false);
	const searchQuery = ref('');

	const formattedOptions = computed(() =>
		props.options.map((option) => {
			const normalized = toMultiselectOption(option);
			return {
				value: normalized.value,
				label: normalized.label,
				icon: normalized.icon,
				count: normalized.count,
				disabled: normalized.disabled ?? false
			};
		})
	);

	const sortedOptions = computed(() => formattedOptions.value);

	const selectItemType = computed(() =>
		props.mode === MultiselectMode.SINGLE ? SelectListItemType.RADIO : SelectListItemType.CHECKBOX
	);

	const mapOptionToSelectItem = (option: IMultiselectOption, isSelected: boolean): ISelectItem => ({
		value: option.value,
		text: option.label,
		icon: option.icon,
		count: option.count,
		state: option.disabled
			? SelectListItemStateType.DISABLED
			: isSelected
				? SelectListItemStateType.SELECTED
				: SelectListItemStateType.ENABLED
	});

	const showOptions = computed(() => (
		!props.searchable ||
		!props.showOptionsOnSearchOnly ||
		searchQuery.value.trim().length > 0
	));

	const visibleOptions = computed(() => (
		showOptions.value && !props.loading ? sortedOptions.value : []
	));

	const handleSearchChange = (query: string): void => {
		searchQuery.value = query ?? '';
		emit('searchChange', searchQuery.value);
	};

	const filterKey = computed(() => `${props.placeholder}-${Date.now()}`);

	const hasSelection = computed(() => {
		if (Array.isArray(multiselectValue.value)) return multiselectValue.value.length > 0;
		return multiselectValue.value !== null && multiselectValue.value !== undefined;
	});

	watch(() => props.modelValue, (val) => {
		multiselectValue.value = val;
	}, { deep: true });

	watch(multiselectValue, (val) => {
		emit('update:modelValue', val);
	}, { deep: true });

	const onOpen = (): void => {
		isOpen.value = true;
		emit('open');
	};

	const onClose = (): void => {
		isOpen.value = false;
		emit('close');
	};

	return {
		multiselectValue,
		isOpen,
		formattedOptions,
		sortedOptions,
		selectItemType,
		mapOptionToSelectItem,
		onOpen,
		onClose,
		searchQuery,
		showOptions,
		visibleOptions,
		handleSearchChange,
		filterKey,
		hasSelection
	};
};
